import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized API client

const ENDPOINT = "/travelData/";

// Fetch all travel records
export const fetchTravelData = createAsyncThunk(
  "travelData/fetchTravelData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(ENDPOINT);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert a new travel record (with file upload)
export const insertTravelData = createAsyncThunk(
  "travelData/insertTravelData",
  async (travelData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("travelType", travelData.travelType);
      formData.append("travelDesc", travelData.travelDesc);
      formData.append("travelSDate", travelData.travelSDate);
      formData.append("travelSTime", travelData.travelSTime);
      formData.append("travelEDate", travelData.travelEDate);
      formData.append("travelETime", travelData.travelETime);
      formData.append("travellerName", travelData.travellerName);
      formData.append("pnr", travelData.pnr);
      formData.append("tc_id", travelData.tc_id);
      formData.append("travelCost", travelData.travelCost);
      formData.append("status", "A");

      if (travelData.travelBill instanceof File) {
        formData.append("travelBill", travelData.travelBill);
      }

      const response = await apiClient.post(ENDPOINT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Insert TravelData error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update an existing travel record
export const updateTravelData = createAsyncThunk(
  "travelData/updateTravelData",
  async (travelData, { rejectWithValue }) => {
    try {
      const { id } = travelData;
      if (!id) throw new Error("Missing travel record id for update");

      const formData = new FormData();
      formData.append("travelType", travelData.travelType);
      formData.append("travelDesc", travelData.travelDesc);
      formData.append("travelSDate", travelData.travelSDate);
      formData.append("travelSTime", travelData.travelSTime);
      formData.append("travelEDate", travelData.travelEDate);
      formData.append("travelETime", travelData.travelETime);
      formData.append("travellerName", travelData.travellerName);
      formData.append("pnr", travelData.pnr);
      formData.append("tc_id", travelData.tc_id);
      formData.append("travelCost", travelData.travelCost);
      formData.append("status", "A");

      if (travelData.travelBill instanceof File) {
        formData.append("travelBill", travelData.travelBill);
      }

      const response = await apiClient.put(`${ENDPOINT}${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Update TravelData error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a travel record
export const deleteTravelData = createAsyncThunk(
  "travelData/deleteTravelData",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${ENDPOINT}${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ───────────────────────── Slice ─────────────────────────

const travelDataSlice = createSlice({
  name: "travelData",
  initialState: {
    travelData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchTravelData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTravelData.fulfilled, (state, action) => {
        state.loading = false;
        state.travelData = action.payload;
      })
      .addCase(fetchTravelData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Insert
      .addCase(insertTravelData.fulfilled, (state, action) => {
        state.travelData.push(action.payload);
      })
      .addCase(insertTravelData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateTravelData.fulfilled, (state, action) => {
        const index = state.travelData.findIndex((td) => td.id === action.payload.id);
        if (index !== -1) {
          state.travelData[index] = action.payload;
        }
      })
      .addCase(updateTravelData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteTravelData.fulfilled, (state, action) => {
        state.travelData = state.travelData.filter((td) => td.id !== action.payload);
      })
      .addCase(deleteTravelData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default travelDataSlice.reducer;
